import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Close as CloseIcon, Email as EmailIcon } from '@mui/icons-material';
import { useState } from 'react';

interface ResetPasswordDialogProps {
  data?: any;
  onClose: (_value: boolean) => void;
  open: boolean;
}

const ResetPasswordDialog = (props: ResetPasswordDialogProps) => {
  const { data, onClose, open } = props;

  const [input, setInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = () => {
    if (loading) return;
    setInput('');
    onClose(false);
  };

  const handleConfirm = async () => {
    if (!input || !input.trim()) {
      return;
    }
    
    if (data?.onConfirm) {
      setLoading(true);
      try {
        const success = await data.onConfirm(input.trim());
        setLoading(false);
        
        if (success !== false) {
          setInput('');
          onClose(false);
        }
      } catch (error) {
        console.error('Error in dialog confirm:', error);
        setLoading(false);
      }
    } else {
      onClose(false);
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canSubmit = input.trim() && isValidEmail(input.trim());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          minWidth: { xs: '90%', sm: 420 },
          maxWidth: 460,
          bgcolor: 'white',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.5,
          pb: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: '#fdf2f8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#E91E8C',
            }}
          >
            {data?.icon || <EmailIcon />}
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '1.1rem',
            }}
          >
            {data?.title || 'Reset Password'}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{
            color: '#94a3b8',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        {data?.description && (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              mb: 2.5,
              lineHeight: 1.6,
            }}
          >
            {data.description}
          </Typography>
        )}

        {data?.setInput && (
          <TextField
            label={data.inputPlaceholder || 'Email address'}
            variant="outlined"
            fullWidth
            type="email"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onInput={(e) => setInput((e.target as HTMLInputElement).value)}
            onPaste={(e) => {
              // Dar tiempo para que el valor se actualice después del paste
              setTimeout(() => {
                const target = e.target as HTMLInputElement;
                setInput(target.value);
              }, 0);
            }}
            disabled={loading}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit && !loading) {
                handleConfirm();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{
              py: 1.25,
              borderRadius: 2,
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
              },
              '&:disabled': {
                borderColor: '#e2e8f0',
                color: '#cbd5e1',
              },
            }}
          >
            {data?.cancelText || 'Cancel'}
          </Button>
          
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || !canSubmit}
            sx={{
              py: 1.25,
              borderRadius: 2,
              bgcolor: '#E91E8C',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(233, 30, 140, 0.25)',
              '&:hover': {
                bgcolor: '#c41874',
                boxShadow: '0 4px 12px rgba(233, 30, 140, 0.35)',
              },
              '&:disabled': {
                bgcolor: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </Box>

        {/* Helper text */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 2,
            color: '#94a3b8',
            fontSize: '0.75rem',
          }}
        >
          We'll send a password reset link to your email
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordDialog;
